import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { contentAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import useListQueryParams from '../hooks/useListQueryParams';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'closed', label: 'Closed' }
];

function formatDate(createdAt) {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const thSx = {
  fontWeight: 700,
  color: 'text.secondary',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const PdfRequests = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '20',
    status: '',
    search: '',
    fromDate: '',
    toDate: '',
    sortOrder: 'desc'
  });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const statusFilter = query.status || '';
  const search = query.search || '';
  const fromDate = query.fromDate || '';
  const toDate = query.toDate || '';
  const sortOrder = query.sortOrder || 'desc';

  const { data, isLoading } = useQuery(
    ['pdf-requests', statusFilter, search, fromDate, toDate, sortOrder, page, limit],
    () => contentAPI.getPdfRequests({
      page,
      limit,
      status: statusFilter || undefined,
      search: search || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      sortOrder
    })
  );

  const requests = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: requests.length };

  const updateStatusMutation = useMutation(
    ({ id, status }) => contentAPI.updatePdfRequest(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pdf-requests');
        showToast({ message: 'PDF request status updated.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to update status',
          severity: 'error'
        })
    }
  );

  return (
    <>
      <PageHeader
        title="PDF Requests"
        subtitle='Leads from the "Get PDF" popup on event cards (name, mobile, event, how can we help).'
      />

      <FilterBar>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setQuery({ status: e.target.value })}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search name / mobile / event / message"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
          sx={{ minWidth: 260 }}
        />
        <AdminDateRangeFilter
          startDate={fromDate}
          endDate={toDate}
          onStartDateChange={(v) => setQuery({ fromDate: v })}
          onEndDateChange={(v) => setQuery({ toDate: v })}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort</InputLabel>
          <Select value={sortOrder} label="Sort" onChange={(e) => setQuery({ sortOrder: e.target.value })}>
            <MenuItem value="desc">Latest first</MenuItem>
            <MenuItem value="asc">Oldest first</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      <Card>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          {isLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : requests.length === 0 ? (
            <EmptyState
              title="No PDF requests yet"
              description="Leads from the Get PDF popup on event cards will show up here."
            />
          ) : (
            <TableContainer sx={{ overflowX: 'auto', mx: -0.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Name</TableCell>
                    <TableCell sx={thSx}>Mobile</TableCell>
                    <TableCell sx={thSx}>Event</TableCell>
                    <TableCell sx={thSx}>How can we help</TableCell>
                    <TableCell sx={thSx}>Date</TableCell>
                    <TableCell sx={thSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                      <TableCell>{row.mobile || '—'}</TableCell>
                      <TableCell>{row.eventTitle || row.eventSlug || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, color: 'text.secondary' }}>
                        {row.message ? (row.message.length > 80 ? `${row.message.slice(0, 80)}…` : row.message) : '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={row.status || 'pending'}
                          onChange={(e) => updateStatusMutation.mutate({ id: row.id, status: e.target.value })}
                          sx={{ minWidth: 130, bgcolor: 'background.paper' }}
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.totalPages > 1 ? (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Button
                size="small"
                variant="outlined"
                disabled={page <= 1}
                onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}
              >
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={page >= pagination.totalPages}
                onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}
              >
                Next
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
};

export default PdfRequests;
